import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, Plus, Minus } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { publicApi } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import HeroPrimed from '../components/HeroPrimed';
import StackConsultingPrime from '../components/StackConsultingPrime';
import EnterpriseBentoGrid from '../components/EnterpriseBentoGrid';
import { IMG, getCategoryImg } from '../lib/images';
import { getTools as getHydratedTools, mergeAndAppendCmsTools } from '../utils/getTools';
import { STACK_PRESCRIPTIONS } from './AIStackPrescriptionsPage';

const HOMEPAGE_CACHE_KEY = 'wowrolla_homepage_data_cache_v1';

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const dedupeTools = (tools = []) => {
  const seen = new Set();
  return tools.filter((tool) => {
    const slug = String(tool?.slug || '').trim().toLowerCase();
    const id = String(tool?.id || '').trim().toLowerCase();
    const name = String(tool?.name || '').trim().toLowerCase();
    const key = slug ? `slug:${slug}` : id ? `id:${id}` : name ? `name:${name}` : '';
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildCategoriesFromTools = (tools = [], limit = 12) => {
  const groups = new Map();
  tools.forEach((tool) => {
    const name = tool.category_name || 'AI Tools';
    const slug = tool.category_slug || slugify(name) || 'ai-tools';
    const id = tool.category_id || slug;

    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name,
        slug,
        icon: '⚙️',
        description: '',
        count: 0,
      });
    }

    groups.get(id).count += 1;
  });

  return Array.from(groups.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(({ id, name, slug, icon, description }) => ({ id, name, slug, icon, description }));
};

const Homepage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredTools, setFeaturedTools] = useState([]);
  const [bentoTools, setBentoTools] = useState([]);
  const [spotlightTool, setSpotlightTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const { getCategoryColor, theme } = useTheme();
  
  const { scrollYProgress } = useScroll();
  const { scrollY } = useScroll();
  const orbOneY = useTransform(scrollY, [0, 2200], [0, -140]);
  const orbTwoY = useTransform(scrollY, [0, 2200], [0, 170]);
  const orbThreeY = useTransform(scrollY, [0, 2200], [0, -90]);
  const isLight = theme === 'light';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      let cached = null;
      try {
        cached = JSON.parse(localStorage.getItem(HOMEPAGE_CACHE_KEY) || 'null');
      } catch {
        cached = null;
      }

      const [catResult, topResult, allToolsResult, settingsResult] = await Promise.allSettled([
        publicApi.getCategories(),
        publicApi.getTopToolsByClicks(10),
        publicApi.getAllTools(false),
        publicApi.getSiteSettings(),
      ]);

      const allTools = allToolsResult.status === 'fulfilled'
        ? (allToolsResult.value.data || [])
        : (cached?.allTools || []);
      const normalizedAllTools = dedupeTools(allTools);

      const loadedCategories = catResult.status === 'fulfilled'
        ? (catResult.value.data || [])
        : (buildCategoriesFromTools(normalizedAllTools).length
          ? buildCategoriesFromTools(normalizedAllTools)
          : (cached?.categories || []));

      const loadedFeaturedTools = topResult.status === 'fulfilled'
        ? dedupeTools(topResult.value.data || []).slice(0, 9)
        : (dedupeTools(normalizedAllTools).slice(0, 9).length ? dedupeTools(normalizedAllTools).slice(0, 9) : (cached?.featuredTools || []));

      const mergedLocalTools = await getHydratedTools({
        fetchCmsTools: async () => normalizedAllTools,
      });
      const mergedBentoTools = dedupeTools(mergeAndAppendCmsTools(mergedLocalTools, normalizedAllTools))
        .filter((tool) => tool.slug)
        .slice(0, 8);

      setCategories(loadedCategories);
      setFeaturedTools(loadedFeaturedTools);
      setBentoTools(mergedBentoTools);

      // Load spotlight tool if enabled
      if (settingsResult.status === 'fulfilled') {
        const settings = settingsResult.value.data || {};
        if (settings.spotlight_enabled && settings.spotlight_tool_id) {
          // Find spotlight tool in allTools
          const spotlight = normalizedAllTools.find(t => (t.id || t.slug) === settings.spotlight_tool_id);
          if (spotlight) {
            setSpotlightTool(spotlight);
          }
        }
      }

      try {
        localStorage.setItem(
          HOMEPAGE_CACHE_KEY,
          JSON.stringify({
            categories: loadedCategories,
            featuredTools: loadedFeaturedTools,
              allTools: normalizedAllTools,
          })
        );
      } catch {
        // Ignore localStorage errors.
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      try {
        const cached = JSON.parse(localStorage.getItem(HOMEPAGE_CACHE_KEY) || 'null');
        if (cached) {
          setCategories(cached.categories || []);
          setFeaturedTools(cached.featuredTools || []);
        }
      } catch {
        // Ignore cache read errors.
      }
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: "What is Wowrolla?",
      answer: "Wowrolla is your comprehensive directory of AI tools for affiliate marketers. We curate the best AI software across multiple categories with detailed reviews, commission structures, and comparison guides."
    },
    {
      question: "How do affiliate programs work?",
      answer: "When you click on our affiliate links and make a purchase, we earn a commission at no extra cost to you. This helps us maintain the platform and continue providing valuable content."
    },
    {
      question: "Are the commission rates accurate?",
      answer: "Yes! We update commission rates, cookie durations, and network information regularly. Each tool listing shows the current affiliate program details including recurring commission status."
    },
    {
      question: "Can I suggest a tool to be added?",
      answer: "Absolutely! We're always looking to expand our directory. Contact us through our admin panel or social media with your suggestions."
    },
    {
      question: "Do you test these tools?",
      answer: "We provide detailed information based on official sources, user reviews, and affiliate program data. Each tool includes pros, cons, and best use cases to help you make informed decisions."
    },
    {
      question: "How do I avoid buying the wrong AI stack?",
      answer: "Start by defining workflows and success metrics first, then compare tools by implementation friction and long-term fit. Wowrolla's finder and consulting page are designed for this exact decision."
    },
    {
      question: "Do commissions bias recommendations?",
      answer: "No. We separate recommendation logic from monetization and keep commission disclosure visible so you can verify incentives before acting."
    }
  ];

  const aiStacksCount = STACK_PRESCRIPTIONS.length;
  const aiStackCostRange = useMemo(() => {
    const values = STACK_PRESCRIPTIONS
      .map((stack) => Number.parseInt(String(stack.annualCost || '').replace(/[^0-9]/g, ''), 10))
      .filter((value) => !Number.isNaN(value));
    if (!values.length) return '$0-$0';
    return `$${Math.min(...values).toLocaleString()}-$${Math.max(...values).toLocaleString()}`;
  }, []);

  // Bento grid layout - varied sizes for visual interest
  const getBentoClass = (index) => {
    const layouts = [
      'md:col-span-2 md:row-span-2', // Large
      'md:col-span-1',
      'md:col-span-1',
      'md:col-span-1',
      'md:col-span-2',
      'md:col-span-1',
    ];
    return layouts[index % layouts.length];
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    setNewsletterStatus('');
    setNewsletterSubmitting(true);
    try {
      const response = await publicApi.newsletterSubscribe({
        email: newsletterEmail,
        source_page: '/',
        role: null,
      });
      setNewsletterStatus(response.data?.message || 'Confirmation email sent. Please confirm your subscription.');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus(error?.response?.data?.detail || 'Subscription failed. Please try again.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-void relative overflow-hidden" data-testid="homepage">
      {/* Soft global parallax atmosphere */}
      <motion.div
        className="pointer-events-none fixed -top-24 -left-20 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-20 z-0"
        style={{ background: 'var(--accent-highlight)', y: orbOneY }}
      />
      <motion.div
        className="pointer-events-none fixed top-[28vh] -right-24 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-20 z-0"
        style={{ background: 'var(--accent-secondary)', y: orbTwoY }}
      />
      <motion.div
        className="pointer-events-none fixed bottom-[-8rem] left-[28%] w-[28rem] h-[28rem] rounded-full blur-3xl opacity-15 z-0"
        style={{ background: 'var(--accent-primary)', y: orbThreeY }}
      />

      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-cyber-yellow z-[100] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      <HeroPrimed />

      {/* Spotlight Tool Banner */}
      <div
        className="relative z-10 w-full py-3 px-4"
        style={{
          background: isLight
            ? 'linear-gradient(90deg, rgba(154,107,0,0.10) 0%, rgba(154,107,0,0.04) 100%)'
            : 'linear-gradient(90deg, rgba(255,232,0,0.12) 0%, rgba(255,232,0,0.04) 100%)',
          borderBottom: isLight
            ? '1px solid rgba(154,107,0,0.24)'
            : '1px solid rgba(255,232,0,0.3)',
        }}
      >
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded"
              style={{
                background: isLight ? 'rgba(154,107,0,0.14)' : 'rgba(255,232,0,0.18)',
                color: isLight ? 'var(--accent-primary)' : 'var(--glow-1)',
                border: isLight ? '1px solid rgba(154,107,0,0.35)' : '1px solid rgba(255,232,0,0.5)',
              }}
            >
              ⚙️ Sponsored Slot
            </span>
            {spotlightTool ? (
              <>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {spotlightTool.name}
                </span>
                {spotlightTool.tagline && (
                  <span className="hidden sm:inline text-sm" style={{ color: 'var(--text-secondary)' }}>
                    - {spotlightTool.tagline}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Reserved for sponsored placement.
              </span>
            )}
          </div>
          {spotlightTool ? (
            <Link
              to={`/tools/${spotlightTool.slug}`}
              className="text-xs font-semibold flex items-center gap-1"
              style={{ color: 'var(--card-premium-cta-text)', textDecoration: 'none' }}
            >
              View tool <ArrowRight size={13} />
            </Link>
          ) : (
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Available</span>
          )}
        </div>
      </div>

      {/* Featured Tools Auto Banner */}
      {featuredTools.length > 0 && (
        <section
          className="py-24 relative z-10 home-featured-bg home-parallax-bg"
          style={{
            backgroundImage: isLight
              ? `linear-gradient(rgba(255,249,236,0.72), rgba(247,252,255,0.84)), url(${IMG.hero.dataNetwork})`
              : `linear-gradient(rgba(8,8,18,0.88), rgba(8,8,18,0.96)), url(${IMG.hero.dataNetwork})`,
            backgroundSize: 'cover',
            backgroundAttachment: 'fixed',
          }}
          data-testid="featured-carousel"
        >
          <div className="container mx-auto px-6 overflow-hidden">
            <div className="text-center mb-16">
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4"
                style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}
              >
                Featured AI Tools
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                A live ribbon of top tools moving at a steady pace
              </p>
            </div>

            <div className="featured-marquee">
              <div className="featured-marquee__track">
                {[...featuredTools, ...featuredTools].map((tool, index) => (
                  <article
                    key={`${tool.id || tool.slug || tool.name}-${index}`}
                    className="featured-marquee-card"
                  >
                    <div className="featured-marquee-card__glow" aria-hidden="true" />
                    <Link to={`/tools/${tool.slug}`} className="block">
                      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{tool.name}</h3>
                      <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                        {tool.tagline || tool.description || 'Top-performing tool trusted by implementation teams.'}
                      </p>
                    </Link>
                    <Link
                      to={`/tools/${tool.slug}`}
                      className="btn-primary-enhanced inline-flex items-center justify-center w-full"
                    >
                      View Tool
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {bentoTools.length > 0 && (
        <section className="py-16 relative z-10" data-testid="enterprise-bento-section">
          <div className="container mx-auto px-6">
            <div className="mb-8">
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-3"
                style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}
              >
                Enterprise Tool Board
              </h2>
              <p className="text-text-secondary max-w-3xl">
                Priority tools get expanded cards so decision-makers can scan high-impact options faster.
              </p>
            </div>
            <EnterpriseBentoGrid tools={bentoTools} />
          </div>
        </section>
      )}

      {/* Bento-Style Categories Grid */}
      <section
        className="py-24 relative z-10 home-parallax-bg"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.70), rgba(247,252,255,0.84)), url(${IMG.hero.categoriesBackdrop})`
            : `linear-gradient(rgba(8,8,18,0.86), rgba(8,8,18,0.95)), url(${IMG.hero.categoriesBackdrop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="categories-section"
      >
        <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-4"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Browse by Category
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Explore our curated collection of AI tools organized by use case
          </p>
        </div>

        {loading ? (
          <div className="text-center text-text-secondary">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
            <p className="mt-4">Loading categories...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[200px] gap-4" data-testid="categories-grid">
            {categories.length === 0 && (
              <div className="md:col-span-4 text-center terminal-grid p-6">
                <p className="text-text-secondary mb-3">Categories are taking longer than expected to load.</p>
                <Link to="/find" className="text-cyber-yellow hover:underline">Use AI Finder instead</Link>
              </div>
            )}
            {categories.map((category, index) => {
              const categoryTheme = getCategoryColor(index, categories.length);
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${getBentoClass(index)}`}
                >
                  <Link
                    to={`/categories/${category.slug}`}
                    className="bento-card block h-full group overflow-hidden relative"
                    style={{
                      background: `linear-gradient(145deg, ${categoryTheme.bg} 0%, var(--bg-elevated) 100%)`,
                      borderColor: categoryTheme.border,
                    }}
                    data-testid={`category-card-${category.slug}`}
                  >
                    <img
                      src={getCategoryImg(index)}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-500 scale-105 group-hover:scale-110"
                      style={{ opacity: 0.10 }}
                    />
                    <div className="h-full flex flex-col justify-between relative z-10">
                      <div>
                        <Sparkles className="mb-4 group-hover:scale-110 transition-transform" style={{ color: categoryTheme.accent }} size={32} />
                        <h3 className="text-xl font-semibold mb-2 transition-colors" style={{ color: categoryTheme.text }}>
                          {category.name}
                        </h3>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {category.description}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xs text-text-muted font-mono">
                          {category.tool_count || 0} tools
                        </span>
                        <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: categoryTheme.accent }} size={16} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
        </div>
      </section>

      {/* AI Stack Prescriptions CTA */}
      <section className="py-16 px-6 container mx-auto max-w-5xl relative z-10" data-testid="ai-stacks-cta-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="terminal-grid p-8 border border-white/10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="md:flex-1">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={24} className="text-cyber-yellow" />
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  See Proven AI Workflows
                </h3>
              </div>
              <p className="text-text-secondary mb-4">
                Stop shopping for tools individually. Explore industry-specific AI stacks that save time and money. For Real Estate, Marketing, E-commerce, Accounting, Healthcare, and Hospitality.
              </p>
              <Link
                to="/ai-stacks"
                className="inline-flex items-center gap-2 text-cyber-yellow hover:text-cyber-yellow/80 font-semibold transition-colors"
                data-testid="homepage-ai-stacks-cta"
              >
                Explore AI Stacks
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="md:text-right">
              <div className={isLight ? 'bg-white border border-[#D8E0EA] p-4 rounded-none mb-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)]' : 'bg-[#0B0C10] border border-white/5 p-4 rounded-none mb-4'}>
                <div className="text-xs text-text-muted uppercase tracking-widest mb-2">ANNUAL COST</div>
                <div className="text-2xl font-mono font-bold text-laser-green">{aiStackCostRange}</div>
              </div>
              <div className={isLight ? 'bg-white border border-[#D8E0EA] p-4 rounded-none shadow-[0_10px_24px_rgba(15,23,42,0.08)]' : 'bg-[#0B0C10] border border-white/5 p-4 rounded-none'}>
                <div className="text-xs text-text-muted uppercase tracking-widest mb-2">STACKS AVAILABLE</div>
                <div className="text-2xl font-bold text-cyber-yellow">{aiStacksCount} Workflows</div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section with Schema */}
      <section
        className="py-24 relative z-10 home-faq-bg home-parallax-bg"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.70), rgba(247,252,255,0.84)), url(${IMG.hero.buyingDecision})`
            : `linear-gradient(rgba(8,8,18,0.82), rgba(8,8,18,0.95)), url(${IMG.hero.buyingDecision})`,
          backgroundSize: 'cover',
        }}
        data-testid="faq-section"
      >
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4"
              style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}
            >
              Frequently Asked Questions
            </h2>
            <p className="text-text-secondary">
              Everything you need to know about Wowrolla
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="faq-item"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="text-lg font-semibold pr-4" style={{ color: 'var(--text-primary)' }}>{faq.question}</span>
                  <div className="flex-shrink-0">
                    {openFaqIndex === index ? (
                      <Minus className="text-cyber-yellow" size={24} />
                    ) : (
                      <Plus className="text-cyber-yellow" size={24} />
                    )}
                  </div>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: openFaqIndex === index ? 'auto' : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 text-text-secondary">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/faq" className="btn-secondary-enhanced inline-flex items-center gap-2" data-testid="homepage-full-faq-link">
              Read Full FAQ
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* FAQ Schema for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </section>

      {/* Newsletter Section */}
      <section
        id="newsletter"
        className="py-24 relative overflow-hidden"
        data-testid="newsletter-section"
      >
        <div
          className="absolute inset-0 z-0 home-newsletter-bg home-parallax-bg"
          style={{
            backgroundImage: isLight
              ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.newsletter})`
              : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.92)), url(${IMG.hero.newsletter})`,
            backgroundSize: 'cover',
          }}
        />
        <div className="absolute inset-0 prime-aurora-bg z-0" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <TrendingUp className="mx-auto mb-6 text-laser-green" size={48} />
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4"
              style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}
            >
              Stay Updated
            </h2>
            <p className="text-text-secondary mb-8">
              Get weekly updates on the latest AI tools, trends, and exclusive insights.
            </p>
            
            <form 
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              onSubmit={handleNewsletterSubscribe}
              data-testid="newsletter-form"
            >
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 px-4 py-3 bg-transparent border border-white/20 rounded-full text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                style={{ color: 'var(--text-primary)' }}
                required
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                className="btn-primary-enhanced"
                disabled={newsletterSubmitting}
                data-testid="newsletter-submit-button"
              >
                {newsletterSubmitting ? 'Submitting...' : 'Subscribe'}
              </button>
            </form>
            {newsletterStatus && (
              <p className="text-sm mt-3" style={{ color: 'var(--accent-primary)' }}>{newsletterStatus}</p>
            )}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-24 relative z-10">
        <StackConsultingPrime />
      </section>
    </div>
  );
};

export default Homepage;

